import type { CookingTechnique } from '@familychef/shared';
import {
  TECHNIQUE_CATEGORIES,
  TECHNIQUE_CATEGORY_LABELS,
  DIFFICULTIES,
  DIFFICULTY_LABELS,
} from '@familychef/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { supabase } from '@/lib/supabase';

interface TechniqueFilters {
  search: string;
  category: string;
  difficulty: string;
}

const PAGE_SIZE = 20;

export function useTechniques(filters: TechniqueFilters, page: number) {
  return useQuery({
    queryKey: ['techniques', filters, page],
    queryFn: async () => {
      let query = supabase
        .from('cooking_techniques')
        .select('*', { count: 'exact' })
        .order('name', { ascending: true })
        .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);

      if (filters.search) query = query.ilike('name', `%${filters.search}%`);
      if (filters.category) query = query.eq('category', filters.category);
      if (filters.difficulty) query = query.eq('difficulty', filters.difficulty);

      const { data, count, error } = await query;
      if (error) throw error;
      return { data: (data ?? []) as CookingTechnique[], total: count ?? 0 };
    },
  });
}

export function useTechnique(id: string) {
  return useQuery<CookingTechnique>({
    queryKey: ['technique', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cooking_techniques')
        .select('*')
        .eq('id', id)
        .single();
      if (error) throw error;
      return data as CookingTechnique;
    },
    enabled: !!id,
  });
}

type TechniqueCreate = Omit<CookingTechnique, 'id' | 'created_at' | 'updated_at'>;

export function useCreateTechnique() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: TechniqueCreate) => {
      const { error } = await supabase.from('cooking_techniques').insert(data);
      if (error) throw error;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['techniques'] }),
  });
}

export function useUpdateTechnique() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<TechniqueCreate> }) => {
      const { error } = await supabase.from('cooking_techniques').update(data).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['techniques'] }),
  });
}

export function useDeleteTechnique() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('cooking_techniques').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['techniques'] }),
  });
}

export { TECHNIQUE_CATEGORIES, TECHNIQUE_CATEGORY_LABELS, DIFFICULTIES, DIFFICULTY_LABELS };
export type { TechniqueFilters };
